import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Select from '@mulesoft/anypoint-components/lib/Select';
import styles from '../AuthenticationFields.css';
import { AWS_REGION_OPTIONS } from '../selectOptions';

/**
 * Amazon Bedrock API Key Authentication Form
 *
 * Renders authentication fields for Amazon Bedrock using API Key (IAM Access Key):
 * - Access Key ID
 * - Secret Access Key (sensitive - cleared in edit mode)
 * - AWS Region
 */
const ApiKeyForm = ({ mode, formData, onChange }) => {
  const awsRegionOptions = useMemo(() => AWS_REGION_OPTIONS, []);

  const handleAccessKeyIdChange = useCallback(
    (e) => onChange('accessKeyId', e.value),
    [onChange]
  );

  const handleSecretAccessKeyChange = useCallback(
    (e) => onChange('secretAccessKey', e.value),
    [onChange]
  );

  const handleAwsRegionChange = useCallback(
    (event) => onChange('awsRegion', event.itemsSelected?.value || ''),
    [onChange]
  );

  return (
    <div
      className={styles['auth-fields']}
      data-test-id="amazon-bedrock-api-key-fields"
    >
      <div className={styles.field}>
        <label htmlFor="access-key-id" className={styles.label}>
          Access Key ID <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="access-key-id"
            name="accessKeyId"
            value={formData.accessKeyId || ''}
            onChange={handleAccessKeyIdChange}
            required
            placeholder="Enter AWS access key ID"
            testId="access-key-id-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="secret-access-key" className={styles.label}>
          Secret Access Key{' '}
          {mode === 'add' && <span className={styles.required}>*</span>}
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="secret-access-key"
            name="secretAccessKey"
            type="password"
            value={formData.secretAccessKey || ''}
            onChange={handleSecretAccessKeyChange}
            required={mode === 'add'}
            placeholder={
              mode === 'edit'
                ? 'Enter new secret to update (leave empty to keep existing)'
                : 'Enter AWS Secret Access Key'
            }
            testId="secret-access-key-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="aws-region" className={styles.label}>
          AWS Region <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="aws-region"
            name="awsRegion"
            testId="aws-region-select"
            value={formData.awsRegion || ''}
            placeholder="Select AWS region"
            options={awsRegionOptions}
            onChange={handleAwsRegionChange}
          />
        </div>
      </div>
    </div>
  );
};

ApiKeyForm.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  formData: PropTypes.shape({
    accessKeyId: PropTypes.string,
    secretAccessKey: PropTypes.string,
    awsRegion: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

ApiKeyForm.defaultProps = {
  mode: 'add'
};

export default ApiKeyForm;
